import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle, Circle, SpeakerHigh, Star, Confetti } from "@phosphor-icons/react";

const speak = (text) => {
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.85;
    window.speechSynthesis.speak(u);
  } catch {}
};

function PhonicsLesson({ content, color }) {
  const l = content.letter;
  const isPair = l.length === 2 && l[0].toLowerCase() === l[1].toLowerCase();
  const upper = isPair ? l[0].toUpperCase() : l;
  const lower = isPair ? l[1].toLowerCase() : l;
  const sayLetter = isPair ? l[0] : l;
  return (
    <div className="text-center" data-testid="lesson-phonics">
      <button onClick={() => speak(sayLetter)} className="mx-auto grid place-items-center w-40 h-40 rounded-[2.5rem] text-white font-heading font-bold text-8xl transition-transform hover:scale-105 active:scale-95" style={{ backgroundColor: color }} data-testid="phonics-letter">
        {content.letter}
      </button>
      <p className="font-body text-xl text-ink-soft mt-6">{content.sound}</p>

      {isPair && (
        <div className="flex justify-center gap-4 mt-7" data-testid="letter-case-audio">
          <button
            onClick={() => speak(`Capital ${upper}`)}
            className="flex flex-col items-center bg-white sticker rounded-3xl px-8 py-5 min-w-[130px] transition-transform hover:-translate-y-1"
            data-testid="uppercase-audio"
          >
            <span className="font-heading font-bold text-6xl" style={{ color }}>{upper}</span>
            <span className="inline-flex items-center gap-1 mt-2 font-body font-semibold text-sm text-ink-soft">
              <SpeakerHigh size={16} weight="duotone" /> Capital letter
            </span>
          </button>
          <button
            onClick={() => speak(`lowercase ${lower}`)}
            className="flex flex-col items-center bg-white sticker rounded-3xl px-8 py-5 min-w-[130px] transition-transform hover:-translate-y-1"
            data-testid="lowercase-audio"
          >
            <span className="font-heading font-bold text-6xl" style={{ color }}>{lower}</span>
            <span className="inline-flex items-center gap-1 mt-2 font-body font-semibold text-sm text-ink-soft">
              <SpeakerHigh size={16} weight="duotone" /> Lowercase letter
            </span>
          </button>
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-4 mt-8">
        {content.words.map((w, i) => (
          <button key={i} onClick={() => speak(w.word)} className="bg-white sticker rounded-3xl px-6 py-5 min-w-[130px] transition-transform hover:-translate-y-1" data-testid={`word-${i}`}>
            <span className="text-5xl block">{w.emoji}</span>
            <span className="font-heading font-bold text-2xl text-ink mt-2 block">{w.word}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SightWordsLesson({ content, color }) {
  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="lesson-sightwords">
      {content.words.map((w, i) => (
        <button key={i} onClick={() => speak(w.word + ". " + w.sentence)} className="w-full flex items-center gap-6 bg-white sticker rounded-3xl p-6 text-left transition-transform hover:-translate-y-1" data-testid={`sightword-${i}`}>
          <span className="grid place-items-center px-6 py-4 rounded-2xl font-heading font-bold text-4xl text-white" style={{ backgroundColor: color }}>{w.word}</span>
          <div>
            <p className="font-body text-lg text-ink">{w.sentence}</p>
            <span className="inline-flex items-center gap-1 mt-1 text-sm font-semibold text-ink-soft"><SpeakerHigh size={16} weight="duotone" /> Tap to hear</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function StoryLesson({ content, color }) {
  const [page, setPage] = useState(0);
  const pages = content.pages;
  return (
    <div className="max-w-2xl mx-auto text-center" data-testid="lesson-story">
      <AnimatePresence mode="wait">
        <motion.div key={page} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
          className="bg-white sticker rounded-[2rem] p-10 min-h-[220px] grid place-items-center">
          <p className="font-heading font-semibold text-3xl text-ink leading-relaxed">{pages[page]}</p>
        </motion.div>
      </AnimatePresence>
      <div className="flex items-center justify-center gap-4 mt-6">
        <Button variant="outline" className="rounded-full border-2" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="story-prev">
          <ArrowLeft size={18} weight="bold" />
        </Button>
        <button onClick={() => speak(pages[page])} className="grid place-items-center w-12 h-12 rounded-full text-white" style={{ backgroundColor: color }} data-testid="story-speak">
          <SpeakerHigh size={22} weight="duotone" />
        </button>
        <span className="font-body font-semibold text-ink-soft">{page + 1} / {pages.length}</span>
        <Button variant="outline" className="rounded-full border-2" disabled={page === pages.length - 1} onClick={() => setPage((p) => p + 1)} data-testid="story-next">
          <ArrowRight size={18} weight="bold" />
        </Button>
      </div>
    </div>
  );
}

function ComprehensionLesson({ content, color }) {
  const [answers, setAnswers] = useState({});
  return (
    <div className="max-w-3xl mx-auto" data-testid="lesson-comprehension">
      <div className="bg-white sticker rounded-3xl p-8 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <p className="font-body font-semibold uppercase tracking-wider text-xs text-ink-soft">Read the passage</p>
          <button onClick={() => speak(content.passage)} className="text-ink-soft hover:text-brand-sky"><SpeakerHigh size={18} weight="duotone" /></button>
        </div>
        <p className="font-body text-lg text-ink leading-relaxed">{content.passage}</p>
      </div>
      <div className="space-y-5">
        {content.questions.map((q, qi) => (
          <div key={qi} className="bg-white sticker rounded-3xl p-6" data-testid={`question-${qi}`}>
            <p className="font-heading font-semibold text-lg text-ink mb-3">{qi + 1}. {q.q}</p>
            <div className="grid gap-2">
              {q.options.map((opt, oi) => {
                const chosen = answers[qi] === oi;
                const correct = oi === q.answer;
                const show = answers[qi] !== undefined;
                return (
                  <button key={oi} onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                    className={`text-left font-body font-medium px-4 py-3 rounded-2xl border-2 transition-colors ${
                      show && correct ? "border-brand-grass bg-brand-grass/10 text-brand-grass"
                      : show && chosen && !correct ? "border-brand-coral bg-brand-coral/10 text-brand-coral"
                      : "border-slate-900/10 text-ink hover:border-brand-sky"}`}
                    data-testid={`q${qi}-opt${oi}`}>
                    {opt}
                    {show && correct && <CheckCircle size={18} weight="fill" className="inline ml-2" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExploreLesson({ content, color }) {
  const [answers, setAnswers] = useState({});
  return (
    <div className="max-w-3xl mx-auto" data-testid="lesson-explore">
      {content.intro && <p className="text-center font-heading font-semibold text-2xl text-ink mb-6">{content.intro}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10">
        {content.cards.map((c, i) => (
          <button key={i} onClick={() => speak(c.say || c.label)} className="bg-white sticker rounded-3xl p-6 flex flex-col items-center transition-transform hover:-translate-y-1" data-testid={`card-${i}`}>
            <span className="text-6xl">{c.emoji}</span>
            <span className="font-heading font-bold text-xl text-ink mt-2">{c.label}</span>
          </button>
        ))}
      </div>
      {content.quiz && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="grid place-items-center w-9 h-9 rounded-xl text-white" style={{ backgroundColor: color }}><Star size={18} weight="fill" /></span>
            <h4 className="font-heading font-bold text-xl text-ink">Let's check what you learned!</h4>
          </div>
          <div className="space-y-5">
            {content.quiz.map((q, qi) => (
              <div key={qi} className="bg-white sticker rounded-3xl p-6" data-testid={`quiz-${qi}`}>
                <p className="font-heading font-semibold text-lg text-ink mb-3">{q.q}</p>
                <div className="grid grid-cols-3 gap-3">
                  {q.options.map((opt, oi) => {
                    const chosen = answers[qi] === oi;
                    const correct = oi === q.answer;
                    const show = answers[qi] !== undefined;
                    return (
                      <button key={oi} onClick={() => { setAnswers((a) => ({ ...a, [qi]: oi })); speak(opt.label); }}
                        className={`rounded-2xl border-2 p-4 flex flex-col items-center transition-colors ${
                          show && correct ? "border-brand-grass bg-brand-grass/10"
                          : show && chosen && !correct ? "border-brand-coral bg-brand-coral/10"
                          : "border-slate-900/10 hover:border-brand-sky"}`}
                        data-testid={`q${qi}-opt${oi}`}>
                        <span className="text-4xl">{opt.emoji}</span>
                        <span className="font-body font-semibold text-sm text-ink mt-1">{opt.label}</span>
                        {show && correct && <CheckCircle size={18} weight="fill" className="text-brand-grass mt-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VowelLesson({ content, color }) {
  const Panel = ({ data, label, badge }) => (
    <div className="bg-white sticker rounded-3xl p-6 flex-1">
      <div className="flex items-center justify-center gap-2 mb-1">
        <span className="px-3 py-1 rounded-full text-xs font-bold text-white uppercase tracking-wide" style={{ backgroundColor: color }}>{badge}</span>
      </div>
      <button
        onClick={() => speak(data.say)}
        className="mx-auto mt-2 flex items-center justify-center gap-2 rounded-2xl border-2 border-slate-900/10 px-5 py-3 font-heading font-semibold text-lg text-ink hover:-translate-y-0.5 transition-transform"
        data-testid={`vowel-${label}-audio`}
      >
        <SpeakerHigh size={22} weight="duotone" style={{ color }} /> {data.sound}
      </button>
      <div className="flex flex-wrap justify-center gap-3 mt-5">
        {data.words.map((w, i) => (
          <button key={i} onClick={() => speak(w.word)} className="bg-slate-50 rounded-2xl px-4 py-3 min-w-[100px] transition-transform hover:-translate-y-1" data-testid={`vowel-${label}-word-${i}`}>
            <span className="text-4xl block">{w.emoji}</span>
            <span className="font-heading font-bold text-lg text-ink mt-1 block">{w.word}</span>
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="max-w-3xl mx-auto" data-testid="lesson-vowels">
      <button onClick={() => speak(content.vowel[0])} className="mx-auto grid place-items-center w-32 h-32 rounded-[2.5rem] text-white font-heading font-bold text-7xl transition-transform hover:scale-105 active:scale-95" style={{ backgroundColor: color }} data-testid="vowel-letter">
        {content.vowel}
      </button>
      <div className="flex flex-col md:flex-row gap-5 mt-8">
        <Panel data={content.short} label="short" badge="Short sound" />
        <Panel data={content.long} label="long" badge="Long sound" />
      </div>
    </div>
  );
}

export default function Reader() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [active, setActive] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [nextPkg, setNextPkg] = useState(null);

  useEffect(() => {
    api.get(`/library/${slug}`).then((r) => {
      setPkg(r.data);
      setCompleted(r.data.completed_lessons || []);
    }).catch(() => { toast.error("You don't own this package yet."); navigate("/catalog"); });
  }, [slug, navigate]);

  useEffect(() => {
    if (!pkg) return;
    const ls = pkg.lessons || [];
    if (ls.length > 0 && ls.every((l) => completed.includes(l.id))) {
      api.get(`/packages/${slug}/next`).then((r) => setNextPkg(r.data.next)).catch(() => {});
    }
  }, [pkg, completed, slug]);

  if (!pkg) return <div className="min-h-[60vh] grid place-items-center font-body text-ink-soft">Loading…</div>;

  if (!pkg.lessons || pkg.lessons.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="reader-no-lessons">
        <h1 className="font-heading font-bold text-2xl text-ink">This item has no online lessons</h1>
        <p className="font-body text-ink-soft mt-2">Head back to your bookshelf.</p>
        <Link to="/library"><Button className="mt-6 rounded-full bg-brand-sky text-white font-bold px-8 py-6">Back to bookshelf</Button></Link>
      </div>
    );
  }

  const lessons = pkg.lessons || [];
  const lesson = lessons[active];
  const done = completed.includes(lesson.id);
  const allDone = lessons.every((l) => completed.includes(l.id));

  const markDone = async () => {
    if (done) return;
    try {
      await api.post("/progress", { package_id: pkg.id, lesson_id: lesson.id });
      setCompleted((c) => [...c, lesson.id]);
      toast.success("Great job! Lesson complete ⭐");
    } catch {}
  };

  const renderLesson = () => {
    const props = { content: lesson.content, color: pkg.color };
    if (lesson.type === "phonics") return <PhonicsLesson {...props} />;
    if (lesson.type === "sightwords") return <SightWordsLesson {...props} />;
    if (lesson.type === "vocabulary") return <SightWordsLesson {...props} />;
    if (lesson.type === "story") return <StoryLesson {...props} />;
    if (lesson.type === "comprehension") return <ComprehensionLesson {...props} />;
    if (lesson.type === "explore") return <ExploreLesson {...props} />;
    if (lesson.type === "vowels") return <VowelLesson {...props} />;
    return null;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="reader-page">
      <Link to="/library" className="inline-flex items-center gap-2 font-body font-semibold text-ink-soft hover:text-brand-sky mb-6">
        <ArrowLeft size={18} weight="bold" /> Back to bookshelf
      </Link>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Lesson list */}
        <aside className="bg-white sticker rounded-3xl p-5 h-fit lg:sticky lg:top-24">
          <p className="font-body text-xs uppercase tracking-wider" style={{ color: pkg.color }}>{pkg.level}</p>
          <h2 className="font-heading font-bold text-xl text-ink mb-4">{pkg.title}</h2>
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1 lg:max-h-none lg:overflow-visible lg:pr-0">
            {lessons.map((l, i) => {
              const c = completed.includes(l.id);
              return (
                <button key={l.id} onClick={() => setActive(i)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-left font-body font-semibold transition-colors ${active === i ? "bg-brand-sky/10 text-brand-sky" : "text-ink hover:bg-slate-50"}`}
                  data-testid={`lesson-nav-${i}`}>
                  {c ? <CheckCircle size={20} weight="fill" className="text-brand-grass shrink-0" /> : <Circle size={20} weight="duotone" className="text-slate-300 shrink-0" />}
                  <span className="text-sm">{l.title}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Content */}
        <div>
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="font-body text-sm text-ink-soft">Lesson {active + 1} of {lessons.length}</p>
              <h1 className="font-heading font-bold text-3xl text-ink tracking-tight">{lesson.title}</h1>
            </div>
            {done && <span className="flex items-center gap-1 px-4 py-2 rounded-full bg-brand-grass/15 text-brand-grass font-bold"><Star size={16} weight="fill" /> Complete</span>}
          </div>

          <div className="min-h-[320px] py-4">{renderLesson()}</div>

          <div className="flex items-center justify-between gap-2 sm:gap-4 mt-10 pt-6 border-t border-slate-900/10">
            <Button variant="outline" className="rounded-full border-2 font-semibold px-4 sm:px-6" disabled={active === 0} onClick={() => setActive((a) => a - 1)} data-testid="prev-lesson">
              <ArrowLeft size={18} weight="bold" className="sm:mr-2" /> <span className="hidden sm:inline">Previous</span>
            </Button>
            <Button onClick={markDone} className={`rounded-full font-bold px-4 sm:px-6 text-white transition-transform hover:-translate-y-0.5 whitespace-nowrap ${done ? "bg-brand-grass" : ""}`} style={done ? {} : { backgroundColor: pkg.color }} data-testid="complete-lesson-btn">
              {done ? <><CheckCircle size={18} weight="fill" className="mr-2" /> <span className="hidden sm:inline">Completed</span><span className="sm:hidden">Done</span></> : "Mark as done ⭐"}
            </Button>
            <Button variant="outline" className="rounded-full border-2 font-semibold px-4 sm:px-6" disabled={active === lessons.length - 1} onClick={() => setActive((a) => a + 1)} data-testid="next-lesson">
              <span className="hidden sm:inline">Next</span> <ArrowRight size={18} weight="bold" className="sm:ml-2" />
            </Button>
          </div>

          {allDone && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="mt-8 relative overflow-hidden rounded-3xl bg-brand-grass p-8 text-center text-white" data-testid="all-done-banner">
              <Confetti size={100} weight="duotone" className="absolute -right-4 -top-4 text-white/20" />
              <h3 className="font-heading font-bold text-2xl relative">🎉 You finished the whole package!</h3>
              <p className="font-body mt-2 relative">Amazing reading, superstar. On to the next adventure!</p>
              <Link to={`/certificate/${pkg.slug}`} data-testid="get-certificate-btn">
                <Button className="mt-4 rounded-full bg-white text-brand-grass hover:bg-brand-sun hover:text-ink font-bold px-8 py-5 relative">
                  <Star size={20} weight="fill" className="mr-2" /> Get your certificate
                </Button>
              </Link>
              {nextPkg && (
                <div className="relative mt-6 bg-white/15 rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-4 text-left" data-testid="next-level-nudge">
                  <img src={nextPkg.image} alt={nextPkg.title} className="w-16 h-16 rounded-2xl object-cover shrink-0" />
                  <div className="flex-1 text-center sm:text-left">
                    <p className="font-body text-xs uppercase tracking-widest text-white/80">Recommended next</p>
                    <p className="font-heading font-bold text-lg text-white">{nextPkg.title}</p>
                    <p className="font-body text-sm text-white/80">{nextPkg.level} · keep the momentum going!</p>
                  </div>
                  <Link to={`/catalog/${nextPkg.slug}`} data-testid="next-level-btn">
                    <Button className="rounded-full bg-white text-brand-grass hover:bg-brand-sun hover:text-ink font-bold px-6">
                      Level up <ArrowRight size={18} weight="bold" className="ml-1" />
                    </Button>
                  </Link>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
